/**
 * ============================================================
 * ASSET : baubeau-engraving.js  v2.8
 * Baubeau Paris — Web Component bb-engraving
 *
 * Changelog v2.8 — Fix badge header (délégation à Horizon via cart:updated) :
 * ─ v2.6 utilisait customElements.get('cart-icon-component')
 *   mais le vrai nom du Custom Element est `cart-icon` —
 *   le patch prototype ne s'appliquait donc jamais.
 * ─ Abandon définitif du monkey-patch prototype — trop fragile.
 * ─ Nouvelle stratégie : _triggerHorizonBadgeUpdate() — fetche /cart.js,
 *   calcule le vrai count (hors frais Baubeau), et écrit
 *   directement dans le DOM du badge via les sélecteurs
 *   confirmés par outerHTML :
 *     · .cart-bubble__text-count  → chiffre visible
 *     · #cart-bubble-text .visually-hidden → texte accessible
 *     · .cart-bubble → bulle entière (masquée si count = 0)
 * ─ Appelée dans _watchCartDeletion après suppression des frais,
 *   et dans _removeOrphanFees après nettoyage.
 * ─ setTimeout(0) : laisse Horizon terminer son propre rendu
 *   avant qu'on écrase sa valeur dans le DOM.
 * ─ Suppression de _feeItemCount, _refreshFeeCount,
 *   et du bloc _patchCartIconSetter — plus nécessaires.
 *
 * Changelog v2.6 — Monkey-patch setter currentCartCount (échec :
 *   mauvais nom de Custom Element).
 * Changelog v2.5 — Fix badge header via querySelector (insuffisant :
 *   Horizon écrasait notre valeur).
 * Changelog v2.4 — Refonte suppression frais (approche proactive).
 * Changelog v2.3 — Fix badge panier (diagnostic définitif).
 * Changelog v2.2 — Fix badge panier (2e tentative).
 * Changelog v2.1 — Fix badge panier non mis à jour.
 * Changelog v2.0 — Fix suppression frais orphelins.
 * Changelog v1.9 — Fix masquage lignes frais.
 * Changelog v1.7 — Migration Horizon 3.4.0.
 *
 * Zéro dépendance externe — Web Component natif
 * ============================================================
 */

const BB_ENGRAVING_VARIANTS = {
  custom_20:   55284315685128,  // BB-ENGRAVING-20  → €20
  custom_40:   55284315717896,  // BB-ENGRAVING-40  → €40
  custom_free: 55284315750664,  // BB-ENGRAVING-FREE → €0
};

const BB_ENGRAVING_LABELS = {
  55284315685128: '+\u202f20\u00a0€ (gravure)',
  55284315717896: '+\u202f40\u00a0€ (gravure)',
  55284315750664: 'Gravure offerte',
};

const BB_FEE_VARIANT_IDS = Object.values(BB_ENGRAVING_VARIANTS).map(String);

let _orphanCheckPending = false;

// ─── Helper ───────────────────────────────────────────────────

function _isFeeVariant(variantId) {
  return BB_FEE_VARIANT_IDS.includes(String(variantId));
}

// ─── Mise à jour badge panier ─────────────────────────────────

/**
 * v2.8 — Délègue la mise à jour du badge à Horizon lui-même
 * en lui fournissant le bon itemCount via cart:updated.
 *
 * Horizon retire la bulle du DOM quand itemCount = 0 via son
 * propre renderCartBubble → onCartUpdate dans cart-icon.js.
 * Toute écriture DOM externe est soit écrasée, soit incomplète
 * (on ne peut pas reproduire la logique de suppression de la
 * bulle qu'Horizon applique en interne).
 *
 * Solution : on fetche /cart.js pour le vrai count (hors frais
 * Baubeau), puis on dispatche cart:updated avec ce count corrigé.
 * Horizon exécute son propre cycle de rendu et retire la bulle
 * si count = 0 — exactement comme pour un produit normal supprimé.
 */
async function _triggerHorizonBadgeUpdate() {
  try {
    const res  = await fetch('/cart.js', { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
    const cart = await res.json();

    const realCount = cart.items
      .filter(i => !_isFeeVariant(i.variant_id))
      .reduce((sum, i) => sum + i.quantity, 0);

    console.log(`[bb-engraving] _triggerHorizonBadgeUpdate → ${realCount} article(s) réel(s)`);

    document.dispatchEvent(new CustomEvent('cart:updated', {
      bubbles: true,
      detail:  { itemCount: realCount },
    }));

    console.log('[bb-engraving] ✅ cart:updated dispatché avec itemCount:', realCount);

  } catch (err) {
    console.warn('[bb-engraving] _triggerHorizonBadgeUpdate échoué:', err);
  }
}

// ─── Masquage JS des lignes frais ────────────────────────────

/**
 * Masque directement par JS toutes les lignes du cart
 * correspondant aux variants frais Baubeau.
 */
function _hideFeeRows() {
  let n = 0;
  BB_FEE_VARIANT_IDS.forEach(id => {
    document.querySelectorAll(`tr[data-key^="${id}"], li[data-key^="${id}"]`).forEach(el => {
      el.style.setProperty('display', 'none', 'important');
      el.classList.add('bb-fee-row-hidden');
      n++;
    });
    document.querySelectorAll(`[data-variant-id="${id}"]`).forEach(el => {
      if (!el.classList.contains('bb-fee-row-hidden')) {
        el.style.setProperty('display', 'none', 'important');
        el.classList.add('bb-fee-row-hidden');
        n++;
      }
    });
  });
  if (n > 0) console.log(`[bb-engraving] ✅ ${n} ligne(s) frais masquée(s)`);
}

// ─── Initialisation globale ───────────────────────────────────

document.addEventListener('DOMContentLoaded', () => {
  _hideFeeRows();
  _watchCartDeletion();
  _observeCartDOM();
  _decorateCartRows();
  // Correction badge au chargement (cas où le cart est déjà peuplé)
  _triggerHorizonBadgeUpdate();
});

document.addEventListener('dialog:open', () => {
  setTimeout(_hideFeeRows, 100);
  setTimeout(_decorateCartRows, 200);
});

document.addEventListener('cart:add', () => {
  setTimeout(_hideFeeRows, 150);
  setTimeout(_decorateCartRows, 350);
  // Corriger le badge après ajout (les frais viennent d'être ajoutés)
  setTimeout(_triggerHorizonBadgeUpdate, 500);
});

// ─── Décoration lignes cart ───────────────────────────────────

async function _decorateCartRows() {
  _hideFeeRows();
  try {
    const res  = await fetch('/cart.js', { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
    const cart = await res.json();

    cart.items.forEach(item => {
      if (!_isFeeVariant(item.variant_id)) return;

      const feeRow = document.querySelector(
        `tr[data-key="${item.key}"], li[data-key="${item.key}"]`
      );
      if (feeRow) {
        feeRow.style.setProperty('display', 'none', 'important');
        feeRow.classList.add('bb-fee-row-hidden');
        console.log('[bb-engraving] ✅ Ligne frais masquée (key exact):', item.key);
      }

      const linkedTo = item.properties?.['__linked_to'];
      const label    = BB_ENGRAVING_LABELS[item.variant_id] || '';
      if (!linkedTo) return;

      const sacRow =
        document.querySelector(`tr[data-key="${linkedTo}"], li[data-key="${linkedTo}"]`) ||
        document.querySelector(`tr[data-key^="${linkedTo.split(':')[0]}"], li[data-key^="${linkedTo.split(':')[0]}"]`);

      if (!sacRow) {
        console.warn('[bb-engraving] Ligne sac introuvable pour key:', linkedTo);
        return;
      }

      sacRow.setAttribute('data-bb-engraving-fee', 'true');
      const details = sacRow.querySelector('.cart-items__details, .cart-item__details');
      if (details) {
        details.setAttribute('data-bb-engraving-label', label);
        const productInfo = details.querySelector('.cart-items__product-info');
        if (productInfo) productInfo.setAttribute('data-bb-engraving-label', label);
      }
      console.log('[bb-engraving] ✅ Sac décoré:', linkedTo, '→', label);
    });
  } catch (err) {
    console.warn('[bb-engraving] _decorateCartRows échoué:', err);
  }
}

// ─── Suppression proactive des frais liés ────────────────────

/**
 * v2.7 — Interception du clic suppression AVANT Horizon.
 * Après suppression des frais, on appelle _triggerHorizonBadgeUpdate()
 * avec un délai suffisant pour laisser Horizon terminer son
 * propre cycle (renderCartBubble) avant qu'on écrase le badge.
 */
function _watchCartDeletion() {
  document.addEventListener('click', async (e) => {
    const deleteBtn = e.target.closest(
      '[data-action="remove"], ' +
      '.cart-items__remove-button, ' +
      'button[name="remove"], ' +
      '.cart-remove-button, ' +
      '.cart-item__remove, ' +
      '[data-cart-item-remove]'
    );
    if (!deleteBtn) return;

    const row = deleteBtn.closest('tr[data-key], li[data-key]');
    if (!row) return;

    const rowKey       = row.dataset.key || '';
    const rowVariantId = Number(rowKey.split(':')[0]);
    if (_isFeeVariant(rowVariantId)) return;

    console.log('[bb-engraving] Clic suppression détecté sur sac:', rowKey);

    try {
      const res  = await fetch('/cart.js', { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
      const cart = await res.json();

      const feeKeys = {};
      cart.items.forEach(item => {
        if (!_isFeeVariant(item.variant_id)) return;
        const linkedTo        = item.properties?.['__linked_to'] || '';
        const linkedVariantId = linkedTo.split(':')[0];
        const rowVariantIdStr = rowKey.split(':')[0];
        if (linkedTo === rowKey || linkedVariantId === rowVariantIdStr) {
          feeKeys[item.key] = 0;
          console.log('[bb-engraving] Frais à supprimer avec le sac:', item.key);
        }
      });

      if (Object.keys(feeKeys).length === 0) {
        console.log('[bb-engraving] Aucun frais lié — pas d\'action');
        return;
      }

      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ updates: feeKeys }),
      });
      console.log('[bb-engraving] ✅ Frais supprimés');

      // Laisser Horizon terminer son renderCartBubble (~300ms),
      // puis écraser le badge avec le vrai count.
      setTimeout(_triggerHorizonBadgeUpdate, 350);

    } catch (err) {
      console.warn('[bb-engraving] Erreur suppression frais liés:', err);
    }

  }, true); // capture:true — on s'exécute avant Horizon
}

// ─── Observer DOM cart ────────────────────────────────────────

function _observeCartDOM() {
  let cartObserver = null;

  const startCartObserver = (cartEl) => {
    if (cartObserver) return;
    cartObserver = new MutationObserver(() => {
      _hideFeeRows();
      setTimeout(_decorateCartRows, 80);
      _scheduleOrphanCheck(600);
    });
    cartObserver.observe(cartEl, { childList: true, subtree: true });
    console.log('[bb-engraving] Observer cart attaché sur:', cartEl.tagName);
  };

  const existing = document.querySelector('cart-drawer-component, cart-items-component');
  if (existing) { startCartObserver(existing); return; }

  const bodyObserver = new MutationObserver(() => {
    const cartEl = document.querySelector('cart-drawer-component, cart-items-component');
    if (cartEl) {
      bodyObserver.disconnect();
      startCartObserver(cartEl);
      _hideFeeRows();
      setTimeout(_decorateCartRows, 150);
      console.log('[bb-engraving] cart-drawer-component détecté');
    }
  });
  bodyObserver.observe(document.body, { childList: true, subtree: true });
}

// ─── Check orphelins ──────────────────────────────────────────

function _scheduleOrphanCheck(delayMs = 500) {
  if (_orphanCheckPending) return;
  _orphanCheckPending = true;
  setTimeout(async () => {
    _orphanCheckPending = false;
    await _removeOrphanFees();
  }, delayMs);
}

async function _removeOrphanFees() {
  try {
    const res  = await fetch('/cart.js', { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
    const cart = await res.json();

    const sacKeys  = cart.items.filter(i => !_isFeeVariant(i.variant_id)).map(i => i.key);
    const feeItems = cart.items.filter(i => _isFeeVariant(i.variant_id));

    const updates = {};
    feeItems.forEach(item => {
      const linkedTo = item.properties?.['__linked_to'];
      if (linkedTo) {
        const sacExists = sacKeys.some(
          key => key === linkedTo || key.startsWith(linkedTo.split(':')[0])
        );
        if (!sacExists) {
          updates[item.key] = 0;
          console.log('[bb-engraving] Frais orphelin à supprimer:', item.key);
        }
      }
    });

    if (Object.keys(updates).length > 0) {
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ updates }),
      });
      console.log('[bb-engraving] ✅ Frais orphelins supprimés');
      // Corriger le badge après nettoyage
      setTimeout(_triggerHorizonBadgeUpdate, 350);
    }

  } catch (err) {
    console.warn('[bb-engraving] Nettoyage orphelins échoué:', err);
  }
}

// ─── Web Component bb-engraving ───────────────────────────────

class BbEngraving extends HTMLElement {

  connectedCallback() {
    this._tag       = this.dataset.engravingTag;
    this._price     = parseInt(this.dataset.engravingPrice, 10) || 0;
    this._sectionId = this.dataset.sectionId;
    this._variantId = parseInt(this.dataset.variantId, 10) || null;
    this._busy      = false;

    this._input        = this.querySelector('.bb-engraving__input');
    this._counter      = this.querySelector('.bb-engraving__counter-current');
    this._confirmation = this.querySelector('.bb-engraving__confirmation');
    this._confirmText  = this.querySelector('.bb-engraving__confirmation-text');

    if (!this._input) return;

    this._input.addEventListener('input', this._onInput.bind(this));
    this._input.addEventListener('blur',  this._onBlur.bind(this));

    this._onVariantChange = this._handleVariantChange.bind(this);
    document.addEventListener('variant:change',  this._onVariantChange);
    document.addEventListener('variant:updated', this._onVariantChange);

    this._input.addEventListener('focus', () => {
      const freshId = this._readVariantFromForm();
      if (freshId) this._variantId = freshId;
    });

    this._hookATCButton();
  }

  disconnectedCallback() {
    if (this._atcButton && this._atcClickHandler) {
      this._atcButton.removeEventListener('click', this._atcClickHandler, true);
    }
    document.removeEventListener('variant:change',  this._onVariantChange);
    document.removeEventListener('variant:updated', this._onVariantChange);
  }

  _handleVariantChange(e) {
    const variant = e.detail?.variant || e.detail?.currentVariant || e.detail;
    if (variant?.id) { this._variantId = variant.id; return; }
    const freshId = this._readVariantFromForm();
    if (freshId) this._variantId = freshId;
  }

  _readVariantFromForm() {
    const form  = this.closest('form[action="/cart/add"]')
               || document.querySelector('form[action="/cart/add"]');
    const input = form?.querySelector('[name="id"]');
    if (input?.value) return parseInt(input.value, 10);

    const sticky = document.querySelector('sticky-add-to-cart');
    if (sticky?.dataset.currentVariantId) return parseInt(sticky.dataset.currentVariantId, 10);

    const variantParam = new URLSearchParams(window.location.search).get('variant');
    if (variantParam) return parseInt(variantParam, 10);
    return null;
  }

  _onInput(e) {
    let val = e.target.value.toUpperCase().replace(/[^A-ZÀ-Ÿ]/g, '').slice(0, 2);
    if (e.target.value !== val) e.target.value = val;
    this._counter.textContent = val.length;
    this._updateState(val);
  }

  _onBlur(e) {
    const val = e.target.value.trim();
    if (val.length >= 1) this._setValid(val);
  }

  _updateState(val) {
    this._input.classList.remove('bb-engraving__input--valid', 'bb-engraving__input--error');
    if (val.length === 0) { this._confirmation.hidden = true; return; }
    this._setValid(val);
  }

  _setValid(val) {
    this._input.classList.add('bb-engraving__input--valid');
    this._confirmation.hidden = false;
    this._confirmText.textContent = this._price > 0
      ? `Gravure « ${val} » — +\u202f${this._price}\u00a0€ ajouté au panier`
      : `Gravure « ${val} » — Offerte 🎁`;
  }

  _hookATCButton() {
    const ATC_SELECTOR =
      '.add-to-cart-button, ' +
      '[data-product-form] button[type="submit"], ' +
      'product-form button[type="submit"], ' +
      'form[action="/cart/add"] button[type="submit"]';

    const attach = () => {
      const section =
        this.closest('[id^="ProductInformation-"]') ||
        this.closest('product-information-component') ||
        this.closest('.product-information') ||
        document;
      const scope = section !== document ? section : document;
      const btn   = scope.querySelector(ATC_SELECTOR);

      if (btn && !btn._bbEngravingHooked) {
        btn._bbEngravingHooked = true;
        this._atcButton        = btn;
        this._atcClickHandler  = this._onATCClick.bind(this);
        btn.addEventListener('click', this._atcClickHandler, true);
        return true;
      } else if (btn && btn._bbEngravingHooked) {
        this._atcButton = btn;
        return true;
      }
      return false;
    };

    if (!attach()) {
      const observer = new MutationObserver(() => { if (attach()) observer.disconnect(); });
      observer.observe(document.body, { childList: true, subtree: true });
      setTimeout(() => observer.disconnect(), 10_000);
    }
  }

  _onATCClick(e) {
    const val = (this._input?.value || '').trim().toUpperCase();
    if (!val) return;

    if (this._busy) { e.preventDefault(); e.stopImmediatePropagation(); return; }

    e.preventDefault();
    e.stopImmediatePropagation();

    const freshId = this._readVariantFromForm();
    if (freshId) this._variantId = freshId;

    const variantId = this._variantId;
    if (!variantId) { console.warn('[bb-engraving] Variant ID introuvable'); return; }

    const form          = this.closest('form[action="/cart/add"]')
                       || document.querySelector('form[action="/cart/add"]');
    const quantityInput = form?.querySelector('[name="quantity"]');
    const quantity      = quantityInput ? parseInt(quantityInput.value, 10) || 1 : 1;

    this._busy = true;
    this._setButtonLoading(true);
    this._addToCartWithEngraving(variantId, quantity, val)
      .finally(() => { this._busy = false; this._setButtonLoading(false); });
  }

  _setButtonLoading(loading) {
    if (!this._atcButton) return;
    if (loading) {
      this._atcButton.setAttribute('disabled', '');
      this._atcButton.setAttribute('aria-busy', 'true');
    } else {
      this._atcButton.removeAttribute('disabled');
      this._atcButton.removeAttribute('aria-busy');
    }
  }

  async _addToCartWithEngraving(variantId, quantity, initiales) {
    const feeVariantId = BB_ENGRAVING_VARIANTS[this._tag];
    if (!feeVariantId) {
      console.warn(`[bb-engraving] Variant frais introuvable pour "${this._tag}"`);
      return;
    }

    try {
      await _removeOrphanFees();

      const sacRes = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({
          id: variantId, quantity,
          properties: { 'Gravure': initiales },
        }),
      });
      if (!sacRes.ok) throw new Error(`Erreur ajout sac: ${sacRes.status}`);
      const sacData = await sacRes.json();

      const feeRes = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({
          id: feeVariantId, quantity: 1,
          properties: { '__linked_to': String(sacData.key || variantId) },
        }),
      });
      if (!feeRes.ok) throw new Error(`Erreur ajout frais: ${feeRes.status}`);

      window.location.reload();

    } catch (err) {
      console.error('[bb-engraving] Erreur Cart API:', err);
      if (this._atcButton) {
        this._atcButton._bbEngravingHooked = false;
        this._atcButton.click();
        this._atcButton._bbEngravingHooked = true;
      }
    }
  }
}

customElements.define('bb-engraving', BbEngraving);